import {deepMerge} from '@sail/utils';

import {captureMessage} from '@sentry/browser';

import {AppConfig, AppProfile} from '../types';

type ClientFetch = typeof fetch;

function authedOrAnonUrl(requiresAuth: boolean, url: string) {
  const apiMatcher = /^\/?api\/(anon\/)?(.*)$/;

  if (requiresAuth) {
    return url.replace(apiMatcher, '/api/$2');
  }

  return url.replace(apiMatcher, '/api/anon/$2');
}

export function authedOrAnonRequest(
  requiresAuth: boolean,
  input: RequestInfo | URL,
): RequestInfo | URL {
  if (typeof input === 'string') {
    return authedOrAnonUrl(requiresAuth, input);
  }

  if ('url' in input) {
    return {
      ...input,
      url: authedOrAnonUrl(requiresAuth, input.url),
    };
  }

  // this shouldn't ever happen, but if it ever did I'd want to know
  captureMessage(
    "ApiProvider expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

// Appends all provided params to the request URL, preserving relative paths.
// Null/undefined values are omitted. All values must be explicitly passed by
// the caller — this helper does not read from any ambient source itself.
function appendQueryParams(
  input: RequestInfo | URL,
  params: Record<string, string | null | undefined>,
): RequestInfo | URL {
  const entries = Object.entries(params).filter(
    (entry): entry is [string, string] => entry[1] != null,
  );

  if (entries.length === 0) return input;

  function augmentUrl(url: string): string {
    const parsed = new URL(url, window.location.origin);
    for (const [key, value] of entries) {
      parsed.searchParams.set(key, value);
    }
    return url.startsWith('/')
      ? `${parsed.pathname}${parsed.search}${parsed.hash}`
      : parsed.toString();
  }

  if (typeof input === 'string') {
    return augmentUrl(input);
  }

  if ('url' in input) {
    return {
      ...input,
      url: augmentUrl(input.url),
    };
  }

  captureMessage(
    "wrapFetch expected an api endpoint url, but didn't find one",
    {level: 'warning'},
  );

  return input;
}

export function wrapFetch(
  fetch: ClientFetch,
  getConfig: () => AppConfig,
  getProfile: () => AppProfile,
  onAuthFailure?: () => void,
): ClientFetch {
  const wrappedFetch: ClientFetch = async (req, init?) => {
    const config = getConfig();
    const {locale} = config;
    const profile = getProfile();
    const {livemode, visitor} = profile;
    const requiresAuth = visitor.sessionType !== 'anonymous';

    const reqWithAnonRoutes = authedOrAnonRequest(requiresAuth, req);

    // stripe-livemode is used by manage
    const livemodeHeaders =
      livemode != null ? {'stripe-livemode': livemode ? 'true' : 'false'} : {};

    const localeHeaders = {
      'Accept-Language': [locale, ...navigator.languages],
    };

    const headers = {
      ...livemodeHeaders,
      ...localeHeaders,
    };

    // @ts-expect-error - Accept-Language value should be a string, not an array
    const mergedInit = deepMerge({headers}, init) as {
      headers: {
        'Accept-Language': string;
        'stripe-livemode'?: string;
      };
    };

    // All query params appended to every outgoing request are listed here explicitly.
    // link_testmode_merchant is read from the iframe's own URL (cross-origin-safe) so
    // that it propagates to support-conversations-srv when testing Link in testmode.
    const reqWithQueryParams = appendQueryParams(reqWithAnonRoutes, {
      referrerLocale: locale || null,
      link_testmode_merchant: new URLSearchParams(window.location.search).get(
        'link_testmode_merchant',
      ),
    });

    const response = await fetch(reqWithQueryParams, mergedInit);
    if (response.status === 401 && requiresAuth && onAuthFailure) {
      onAuthFailure();
    }
    return response;
  };
  return wrappedFetch;
}
